import { ChatProviderType } from '../../../domain/chat/models/ChatProviderType';
import { ChatSession } from '../../../domain/chat/models/ChatSession';
import { ChatDocument } from '../../../domain/documents/models/ChatDocument';
import { ChatMessage } from '../../../domain/messages/models/ChatMessage';
import { ProviderServices } from '../../../services/chat/createProviderServices';
import { mapProGPTAgentToMetadata } from '../../../services/providers/progpt/mappers/mapProGPTAgentToMetadata';
import { LoadInitialChatDataHandlerResult } from '../models/LoadInitialChatDataHandlerResult';
import { CHAT_MESSAGES_PAGE_SIZE, supportsMessagePagination } from '../../chat-messages/chatMessagesPaging';

export interface LoadInitialChatDataHandlerInput {
    accessToken: string;
    nexusAccessToken: string;
    providerServices: ProviderServices;
}

const sortByMostRecentlyUsed = (sessions: ChatSession[]): ChatSession[] => {
    return [...sessions].sort(
        (left, right) => (right.modifiedDate ?? right.createdDate) - (left.modifiedDate ?? left.createdDate),
    );
};

const ensureInitialSessionAsync = async (
    sessions: ChatSession[],
    accessToken: string,
    providerServices: ProviderServices,
): Promise<ChatSession[]> => {
    if (sessions.length > 0) {
        return sessions;
    }

    const createdSession = await providerServices.proGPT.sessionWrite.createChatAsync(
        {
            providerType: ChatProviderType.ProGPT,
            title: 'New ProGPT Chat',
        },
        accessToken,
    );

    return [createdSession.session];
};

// get the messages for the most recently used session to display in the UI when the app is opened
const loadSelectedSessionMessagesAsync = async (
    sessions: ChatSession[],
    accessToken: string,
    providerServices: ProviderServices,
): Promise<{ selectedSessionId: string | null; selectedSessionMessages: ChatMessage[]; selectedSessionHasMoreMessages: boolean }> => {
    const selectedSession = sessions[0];

    if (!selectedSession) {
        return {
            selectedSessionId: null,
            selectedSessionMessages: [],
            selectedSessionHasMoreMessages: false,
        };
    }

    const selectedProviderServices = providerServices.providerServicesRouter.getProviderServices(
        selectedSession.providerType,
    );

    const selectedSessionMessages = await selectedProviderServices.messages.getChatMessagesAsync(
        {
            chatId: selectedSession.id,
            skip: 0,
            count: CHAT_MESSAGES_PAGE_SIZE,
        },
        accessToken,
    );

    return {
        selectedSessionId: selectedSession.id,
        selectedSessionMessages,
        selectedSessionHasMoreMessages: supportsMessagePagination(selectedSession.providerType) && selectedSessionMessages.length === CHAT_MESSAGES_PAGE_SIZE,
    };
};

const loadSelectedSessionDocumentsAsync = async (
    selectedSession: ChatSession | undefined,
    accessToken: string,
    providerServices: ProviderServices,
    documentsBySessionId: Record<string, ChatDocument[]>,
): Promise<Record<string, ChatDocument[]>> => {
    if (!selectedSession || documentsBySessionId[selectedSession.id]) {
        return documentsBySessionId;
    }

    // nexus documents are already loaded in the getAllChatsAsync call, so we only need to load progpt documents here since progpt returns those separately
    if (selectedSession.providerType !== ChatProviderType.ProGPT) {
        return documentsBySessionId;
    }

    const documents = await providerServices.proGPT.documents.getDocumentsAsync(selectedSession.id, accessToken);

    return {
        ...documentsBySessionId,
        [selectedSession.id]: documents,
    };
};

export const loadInitialChatDataForRestrictedUserHandler = async ({
    accessToken,
    providerServices
}: LoadInitialChatDataHandlerInput): Promise<LoadInitialChatDataHandlerResult> => {

    // get only ProGPT chat sessions and agents since restricted users don't have access to Nexus or Assistants, but allow it to fail gracefully in case ProGPT is down and show the service unavailable state
    const [proGPTSessionsResult] = await Promise.allSettled([providerServices.proGPT.sessions.getAllChatsAsync(accessToken)]);

    const loadedSessions = sortByMostRecentlyUsed(proGPTSessionsResult.status === 'fulfilled' ? proGPTSessionsResult.value.sessions : []);
    const documentsBySessionId = {
        ...(proGPTSessionsResult.status === 'fulfilled' ? proGPTSessionsResult.value.documentsBySessionId : {})
    };
    const agents = [
        mapProGPTAgentToMetadata(), // get default progpt metadata since its not coming from api
    ];

    const providerLoadErrors: string[] = [
        ...(proGPTSessionsResult.status === 'rejected' ? ['Failed to load ProGPT chats.'] : [])
    ];


    // load the most recent chats messages and documents
    try {

        // only create chat session in the case that the user has no chat sessions. If providers are down, this will throw and we show the service unavailable state
        const sessions = await ensureInitialSessionAsync(loadedSessions, accessToken, providerServices);
        const selectedSessionLoad = await loadSelectedSessionMessagesAsync(sessions, accessToken, providerServices);
        const hydratedDocumentsBySessionId = await loadSelectedSessionDocumentsAsync(
            sessions[0],
            accessToken,
            providerServices,
            documentsBySessionId,
        );

        return {
            sessions,
            agents,
            documentsBySessionId: hydratedDocumentsBySessionId,
            selectedSessionId: selectedSessionLoad.selectedSessionId,
            selectedSessionMessages: selectedSessionLoad.selectedSessionMessages,
            selectedSessionHasMoreMessages: selectedSessionLoad.selectedSessionHasMoreMessages,
            providerLoadErrors,
            isServiceUnavailable: false,
        };
    } catch {
        return {
            sessions: loadedSessions,
            agents,
            documentsBySessionId,
            selectedSessionId: loadedSessions[0]?.id ?? null,
            selectedSessionMessages: [],
            selectedSessionHasMoreMessages: false,
            providerLoadErrors,
            // all session providers failed AND the fallback createChatAsync also threw — no usable sessions
            isServiceUnavailable: loadedSessions.length === 0,
        };
    }
}
// entry point handler for loading initial chat data when user opens the app
// loads chat sessions from all providers, messages and documents for the most recently used session, and agent metadata
export const loadInitialChatDataHandler = async ({
    accessToken,
    nexusAccessToken,
    providerServices
}: LoadInitialChatDataHandlerInput): Promise<LoadInitialChatDataHandlerResult> => {

    // get all chat sessions and agents in parallel, but allow partial success if one or more providers fail
    const [proGPTSessionsResult, nexusSessionsResult, assistantsSessionsResult, nexusAgentsResult] = await Promise.allSettled([
        providerServices.proGPT.sessions.getAllChatsAsync(accessToken),
        providerServices.nexus.sessions.getAllChatsAsync(nexusAccessToken),
        providerServices.assistants.sessions.getAllChatsAsync(accessToken),
        providerServices.nexus.agents.getAgentsAsync(nexusAccessToken),
    ]);

    const loadedSessions = sortByMostRecentlyUsed([
        ...(proGPTSessionsResult.status === 'fulfilled' ? proGPTSessionsResult.value.sessions : []),
        ...(nexusSessionsResult.status === 'fulfilled' ? nexusSessionsResult.value.sessions : []),
        ...(assistantsSessionsResult.status === 'fulfilled' ? assistantsSessionsResult.value.sessions : []),
    ]);
    const documentsBySessionId = {
        ...(proGPTSessionsResult.status === 'fulfilled' ? proGPTSessionsResult.value.documentsBySessionId : {}),
        ...(nexusSessionsResult.status === 'fulfilled' ? nexusSessionsResult.value.documentsBySessionId : {}),
        ...(assistantsSessionsResult.status === 'fulfilled' ? assistantsSessionsResult.value.documentsBySessionId : {}),
    };
    const agents = [
        mapProGPTAgentToMetadata(), // get default progpt metadata since its not coming from api
        ...(nexusAgentsResult.status === 'fulfilled' ? nexusAgentsResult.value.agents : []),
    ];

    const providerLoadErrors: string[] = [
        ...(proGPTSessionsResult.status === 'rejected' ? ['Failed to load ProGPT chats.'] : []),
        ...(nexusSessionsResult.status === 'rejected' ? ['Failed to load Nexus chats.'] : []),
        ...(assistantsSessionsResult.status === 'rejected' ? ['Failed to load Assistants chats.'] : []),
        ...(nexusAgentsResult.status === 'rejected' ? ['Failed to load agents.'] : []),
    ];


    // load the most recent chats messages and documents
    try {

        // only create chat session in the case that the user has no chat sessions. If providers are down, this will throw and we show the service unavailable state
        const sessions = await ensureInitialSessionAsync(loadedSessions, accessToken, providerServices);
        const selectedSessionLoad = await loadSelectedSessionMessagesAsync(sessions, accessToken, providerServices);
        const hydratedDocumentsBySessionId = await loadSelectedSessionDocumentsAsync(
            sessions[0],
            accessToken,
            providerServices,
            documentsBySessionId,
        );

        return {
            sessions,
            agents,
            documentsBySessionId: hydratedDocumentsBySessionId,
            selectedSessionId: selectedSessionLoad.selectedSessionId,
            selectedSessionMessages: selectedSessionLoad.selectedSessionMessages,
            selectedSessionHasMoreMessages: selectedSessionLoad.selectedSessionHasMoreMessages,
            providerLoadErrors,
            isServiceUnavailable: false,
        };
    } catch {
        return {
            sessions: loadedSessions,
            agents,
            documentsBySessionId,
            selectedSessionId: loadedSessions[0]?.id ?? null,
            selectedSessionMessages: [],
            selectedSessionHasMoreMessages: false,
            providerLoadErrors,
            // all session providers failed AND the fallback createChatAsync also threw — no usable sessions
            isServiceUnavailable: loadedSessions.length === 0,
        };
    }
};

export default loadInitialChatDataHandler;
